import _ from 'lodash'

export const useEporner = () => {
	const {getFileNameFromUrl, extFetch} = useHelpers()

	/**
	 * Generate hash for eporner API request
	 * @param {string} hash - 32-character hex hash from video page
	 * @returns {string} - Generated hash for API
	 */
	function generateApiHash(hash) {
		if (!hash || hash.length !== 32) {
			throw new Error('Invalid hash: must be 32 characters long');
		}

		return parseInt(hash.substring(0, 8), 16).toString(36) +
			   parseInt(hash.substring(8, 16), 16).toString(36) +
			   parseInt(hash.substring(16, 24), 16).toString(36) +
			   parseInt(hash.substring(24, 32), 16).toString(36);
	}

	/**
	 * Extract video ID and hash from eporner URL
	 * @param {string} url - eporner video URL
	 * @returns {object} - Object with videoId
	 */
	function parseEpornerUrl(url) {
		try {
			const urlObj = new URL(url);
			const pathParts = urlObj.pathname.split('/').filter(part => part);

			// Find the video ID part (should start with 'video-')
			const videoPart = pathParts.find(part => part.startsWith('video-'));
			if (!videoPart) {
				throw new Error('Invalid eporner URL format: could not find video ID');
			}

			const videoId = videoPart.replace('video-', '');
			return { videoId };
		} catch (e) {
			console.error('[useEporner] Error parsing URL:', url, e);
			throw e;
		}
	}

	/**
	 * Extract hash from HTML content
	 * @param {string} html - HTML content of video page
	 * @returns {string} - Video hash
	 */
	function extractHashFromHtml(html) {
		// Look for 32-character hexadecimal hash in the HTML
		const hashRegex = /\b[a-f0-9]{32}\b/;
		const hashMatch = html.match(hashRegex);

		if (hashMatch) {
			return hashMatch[0];
		}

		// Fallback: look for hash in script assignments
		const scriptHashMatch = html.match(/EP\.video\.player\.hash\s*=\s*['"]([^'"]+)['"]/);
		if (scriptHashMatch) {
			return scriptHashMatch[1];
		}

		throw new Error('Could not extract video hash from HTML');
	}

	/**
	 * Extract video URL from eporner page
	 */
	async function getVideoFromUrl(url) {
		console.log('[useEporner] Processing URL:', url);
		try {
			// Parse URL to get video ID
			const { videoId } = parseEpornerUrl(url);
			
			// 1. Get the video page to extract the hash
			const pageResponse = await extFetch(url, {
				method: 'GET',
				headers: {
					'User-Agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/143.0.0.0 Safari/537.36',
					'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,image/avif,image/webp,*/*;q=0.8',
				}
			});

			if (!pageResponse || !pageResponse.body) {
				throw new Error('Failed to fetch video page');
			}

			const html = typeof pageResponse.body === 'string' ? pageResponse.body : (pageResponse.body.toString ? pageResponse.body.toString() : String(pageResponse.body));
			const hash = extractHashFromHtml(html);
			console.log('[useEporner] Extracted hash:', hash);

			// 2. Fetch video info from API
			const apiHash = generateApiHash(hash);
			const timestamp = Date.now();
			const params = {
				hash: apiHash,
				domain: 'www.eporner.com',
				pixelRatio: 2,
				playerWidth: 0,
				playerHeight: 0,
				fallback: false,
				embed: false,
				supportedFormats: 'hls,dash,h265,vp9,av1,mp4',
				_: timestamp
			};

			const queryString = Object.keys(params)
				.map(key => `${key}=${encodeURIComponent(params[key])}`)
				.join('&');

			const apiUrl = `https://www.eporner.com/xhr/video/${videoId}?${queryString}`;
			
			const apiResponse = await extFetch(apiUrl, {
				method: 'GET',
				headers: {
					'User-Agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/143.0.0.0 Safari/537.36',
					'Referer': url,
					'Accept': '*/*',
				}
			});

			if (!apiResponse || !apiResponse.body) {
				throw new Error('Failed to fetch video info from API');
			}

			const videoInfo = apiResponse.body;
			if (!videoInfo.available) {
				throw new Error('Video is not available: ' + (videoInfo.message || 'Unknown error'));
			}

			// 3. Select best quality (mp4)
			const mp4Sources = videoInfo.sources.mp4;
			const availableQualities = Object.keys(mp4Sources);
			const qualities = ['1080p', '720p', '480p', '360p', '240p'];
			
			let selectedQuality = availableQualities[0]; // Default to first available
			for (const q of qualities) {
				if (mp4Sources[q]) {
					selectedQuality = q;
					break;
				}
			}

			const videoSrc = mp4Sources[selectedQuality].src;
			console.log('[useEporner] Selected source:', selectedQuality, videoSrc);

			let fileName = `${videoId}-${selectedQuality}.mp4`;

			return {
				website: 'eporner.com',
				type: 'video',
				url: videoSrc,
				fileName: fileName,
				headers: {
					'Referer': 'https://www.eporner.com/',
					'User-Agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/143.0.0.0 Safari/537.36',
				},
				previewUrl: videoSrc,
				thumbnail: videoInfo.thumbs?.['160x120']?.[0]?.src || null,
				canView: true,
				quality: selectedQuality
			};

		} catch (error) {
			console.error('[useEporner] Error fetching video from URL:', error);
			return {
				website: 'eporner.com',
				type: 'video',
				url: url,
				fileName: 'video.mp4',
				previewUrl: url,
				thumbnail: null,
				canView: true,
				error: error.message
			};
		}
	}

	/**
	 * Process multiple URLs and extract videos
	 */
	async function getVideosFromUrls(urls) {
		let validUrls = urls
			.split('\n')
			.map(url => url.trim())
			.filter(url => url && url.length > 0)

		const CONCURRENCY = 5
		const results = new Array(validUrls.length)
		let nextIndex = 0

		const worker = async () => {
			while (nextIndex < validUrls.length) {
				const idx = nextIndex++
				results[idx] = await getVideoFromUrl(validUrls[idx])
			}
		}

		await Promise.all(
			Array.from({length: Math.min(CONCURRENCY, validUrls.length)}, () => worker())
		)

		return results.map((media, index) => ({
			...media,
			id: `eporner-${Date.now()}-${index}`,
			source: 'eporner'
		}))
	}

	return {
		getMedia: async (form) => {
			if (form.urls) return getVideosFromUrls(form.urls)
			if (form.url) {
				let media = await getVideoFromUrl(form.url)
				media.id = `eporner-${Date.now()}`
				media.source = 'eporner'
				return [media]
			}
			return []
		},
		getVideoFromUrl,
		getVideosFromUrls
	}
}
